import { RECONNECT_WINDOW_MS } from '@phidio/protocol';
import { engine } from '../hooks/useEngine.ts';
import type { ReconnectState } from '../lib/engine.ts';
import { HeartIcon, ReplayIcon } from './Icons.tsx';

/**
 * Reunions.
 *
 * A spark used to be a dead end — a nice moment you could never act on. The pact
 * minted by the server turns it into a door that stays open for twelve hours,
 * and these two surfaces are that door: an invitation you can accept from
 * anywhere, and a waiting room while the other side decides.
 */

/** Shown app-wide the moment a past spark asks for you. */
export function ReconnectPrompt({ reconnect }: { reconnect: ReconnectState | null }) {
  if (!reconnect || reconnect.direction !== 'incoming') return null;

  return (
    <div
      className="pointer-events-none fixed inset-x-0 top-0 z-[60] flex justify-center p-4"
      style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}
      role="alertdialog"
      aria-label="Reconnect request"
    >
      <div className="glass pointer-events-auto relative w-full max-w-sm overflow-hidden rounded-3xl p-5 shadow-2xl ring-1 ring-phidio-500/40 animate-rise">
        <span
          className="countdown-bar absolute inset-x-0 top-0 h-0.5 bg-phidio-500"
          style={{ '--countdown': `${RECONNECT_WINDOW_MS}ms` } as React.CSSProperties}
          aria-hidden
        />

        <div className="flex items-center gap-3">
          <span className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-phidio-400 to-phidio-700 text-white">
            <span className="absolute inset-0 rounded-full bg-phidio-500/60 animate-pulse-ring" />
            <HeartIcon filled width={22} height={22} />
          </span>
          <div>
            <p className="text-base font-semibold">{reconnect.name} wants to talk again</p>
            <p className="text-sm text-white/55">You two sparked. They're online right now.</p>
          </div>
        </div>

        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={() => void engine.acceptReconnect()}
            className="flex-1 rounded-full bg-gradient-to-br from-phidio-400 to-phidio-600 px-4 py-3 text-sm font-bold uppercase tracking-wide text-white transition active:scale-95"
          >
            Talk now
          </button>
          <button
            type="button"
            onClick={() => engine.declineReconnect()}
            className="rounded-full bg-white/8 px-4 py-3 text-sm font-medium text-white/70 transition hover:bg-white/16"
          >
            Not now
          </button>
        </div>
      </div>
    </div>
  );
}

/** Replaces the search spinner while we wait for one specific person. */
export function ReunionOverlay({ name }: { name: string }) {
  return (
    <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-6 bg-ink-950/85 px-8 text-center backdrop-blur-md">
      <span className="relative flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-phidio-400 to-phidio-700 text-white">
        <span className="absolute inset-0 rounded-full bg-phidio-500/50 animate-pulse-ring" />
        <span className="absolute inset-0 rounded-full bg-phidio-500/40 animate-pulse-ring [animation-delay:-1.2s]" />
        <ReplayIcon width={38} height={38} />
      </span>

      <div>
        <h2 className="text-2xl font-light">
          Ringing <span className="font-semibold text-phidio-400">{name}</span>
        </h2>
        <p className="mt-2 max-w-xs text-sm text-white/50">
          They'll drop into this call the moment they accept.
        </p>
      </div>

      <button
        type="button"
        onClick={() => engine.declineReconnect()}
        className="rounded-full bg-white/8 px-6 py-2.5 text-sm font-medium text-white/70 transition hover:bg-white/16"
      >
        Cancel
      </button>
    </div>
  );
}
